import React, { useId, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { FloatLabel } from "primereact/floatlabel";
import {
  combineThresholdMinutes,
  splitThresholdMinutes,
} from "../configs/featuredBadgeRuleHelpers";

interface ThresholdDurationPickerProps {
  id?: string;
  value: number;
  onChange: (minutes: number) => void;
  disabled?: boolean;
  className?: string;
  maxDays?: number;
  minMinutes?: number;
  maxMinutes?: number;
  hint?: string | null;
  description?: string | null;
  label?: string;
  labelRequired?: boolean;
}

const clampTotal = (total: number, min: number | undefined, max: number | undefined): number => {
  let clamped = total;
  if (min != null && clamped < min) {
    clamped = min;
  }
  if (max != null && clamped > max) {
    clamped = max;
  }
  return clamped;
};

const formatFieldLabel = (label: string, required?: boolean): string =>
  required ? `${label} *` : label;

const spinnerButtonProps = {
  showButtons: true as const,
  buttonLayout: "vertical" as const,
  incrementButtonIcon: "pi pi-chevron-up",
  decrementButtonIcon: "pi pi-chevron-down",
  incrementButtonClassName: "p-button-text p-button-plain p-0",
  decrementButtonClassName: "p-button-text p-button-plain p-0",
  inputClassName: "text-center border-none shadow-none px-0 py-0",
  inputStyle: { fontSize: "1.1rem", textAlign: "center" as const },
};

export const ThresholdDurationPicker: React.FC<ThresholdDurationPickerProps> = ({
  id,
  value,
  onChange,
  disabled,
  className,
  maxDays = 365,
  minMinutes,
  maxMinutes,
  hint,
  description,
  label,
  labelRequired,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const { days, hours, minutes } = splitThresholdMinutes(value);

  const display =
    days > 0
      ? `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
      : `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;

  const updatePart = (patch: Partial<{ days: number; hours: number; minutes: number }>) => {
    const rawTotal = combineThresholdMinutes({
      days,
      hours,
      minutes,
      ...patch,
    });
    onChange(clampTotal(rawTotal, minMinutes, maxMinutes));
  };

  const minParts = minMinutes != null ? splitThresholdMinutes(minMinutes) : undefined;
  const maxParts = maxMinutes != null ? splitThresholdMinutes(maxMinutes) : undefined;

  const effectiveMaxDays = maxParts != null ? Math.min(maxDays, maxParts.days) : maxDays;
  const effectiveMinDays = minParts != null ? minParts.days : 0;

  const computeHoursBounds = (): { minHours: number; maxHours: number } => {
    let minH = 0;
    let maxH = 23;
    if (minParts != null && days === minParts.days) {
      minH = minParts.hours;
    }
    if (maxParts != null && days === maxParts.days) {
      maxH = maxParts.hours;
    }
    return { minHours: minH, maxHours: maxH };
  };

  const computeMinutesBounds = (): { minMins: number; maxMins: number } => {
    let minM = 0;
    let maxM = 59;
    if (minParts != null && days === minParts.days && hours === minParts.hours) {
      minM = minParts.minutes;
    }
    if (maxParts != null && days === maxParts.days && hours === maxParts.hours) {
      maxM = maxParts.minutes;
    }
    return { minMins: minM, maxMins: maxM };
  };

  const { minHours, maxHours } = computeHoursBounds();
  const { minMins, maxMins } = computeMinutesBounds();

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setIsOpen((current) => !current);
  };

  const hintMessage =
    hint != null && hint !== "" ? (
      <small className="text-red-500 line-height-3">
        <i className="pi pi-info-circle mr-1 text-xs" />
        {hint}
      </small>
    ) : null;

  const descriptionMessage =
    description != null && description !== "" ? (
      <small className="text-500 line-height-3 block">{description}</small>
    ) : null;

  const renderSpinner = (
    fieldLabel: string,
    fieldValue: number,
    onValueChange: (event: InputNumberValueChangeEvent) => void,
    min: number,
    max: number
  ) => (
    <div className="col-4 flex flex-column align-items-center gap-1 px-1">
      <span className="text-500 text-xs white-space-nowrap">{fieldLabel}</span>
      <InputNumber
        value={fieldValue}
        onValueChange={onValueChange}
        min={min}
        max={max}
        disabled={disabled}
        className="threshold-duration-spinner"
        {...spinnerButtonProps}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-2 w-full">
      <style>{`
        .threshold-duration-panel .threshold-duration-spinner.p-inputnumber {
          display: inline-flex;
          width: 3rem;
          max-width: 100%;
        }

        .threshold-duration-panel .threshold-duration-spinner.p-inputnumber-buttons-vertical {
          flex-direction: column;
          align-items: stretch;
        }

        .threshold-duration-panel .threshold-duration-spinner.p-inputnumber-buttons-vertical .p-inputnumber-input {
          width: 100%;
          min-width: 0;
          text-align: center;
        }

        .threshold-duration-panel .threshold-duration-spinner.p-inputnumber-buttons-vertical .p-inputnumber-button {
          width: 100%;
          justify-content: center;
        }
      `}</style>
      <FloatLabel className="w-full">
        <InputText
          id={id}
          readOnly
          value={display}
          className={className}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (disabled) {
              return;
            }
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsOpen((current) => !current);
            }
          }}
          style={{ cursor: disabled ? undefined : "pointer" }}
        />
        {label ? <label htmlFor={id}>{formatFieldLabel(label, labelRequired)}</label> : null}
      </FloatLabel>

      {descriptionMessage}
      {hintMessage}

      {isOpen ? (
        <div
          id={panelId}
          className="threshold-duration-panel surface-card border-1 surface-border border-round p-2 w-full overflow-hidden"
        >
          <div className="grid grid-nogutter w-full">
            {renderSpinner("Días", days, (event) => {
              const nextDays = Math.min(Math.max(event.value || 0, effectiveMinDays), effectiveMaxDays);
              updatePart({ days: nextDays });
            }, effectiveMinDays, effectiveMaxDays)}
            {renderSpinner("Horas", hours, (event) => {
              const nextHours = Math.min(Math.max(event.value || 0, minHours), maxHours);
              updatePart({ hours: nextHours });
            }, minHours, maxHours)}
            {renderSpinner("Minutos", minutes, (event) => {
              const nextMinutes = Math.min(Math.max(event.value || 0, minMins), maxMins);
              updatePart({ minutes: nextMinutes });
            }, minMins, maxMins)}
          </div>
        </div>
      ) : null}
    </div>
  );
};
